import { useState } from 'react';
import { StructuredDataView, colors } from '@resida/ui';
import type { TimelineItem } from '../../api/repairs.api';
import { usePropertyTimezone } from '../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import { ACTIVITY_EVENT_LABELS, attachmentActivityLines } from '../../utils/repairActivity';

/** Fallback label for a lifecycle event with no ACTIVITY_EVENT_LABELS entry: strips the
 *  dotted namespace and reads the rest as a sentence ("repair.categorized" -> "Repair
 *  categorized"). The raw code stays available as the row's tooltip. */
function humanizeEventType(eventType: string): string {
  const words = eventType.replace(/[._]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : eventType;
}

/** Lifecycle event — rendered as a quiet centered marker between messages (as in the design
 *  mockup) rather than a card, so the conversation reads as messages with the odd milestone,
 *  not an audit log. Photo events show a readable count/reason list; every other payload/result
 *  stays one click away under "Show details". */
export function ActivityRow({ item }: { item: TimelineItem }) {
  const propertyTimezone = usePropertyTimezone();
  const [expanded, setExpanded] = useState(false);
  const detailLines = attachmentActivityLines(item.eventType, item.payload);
  const hasPayload = detailLines.length === 0 && item.payload && Object.keys(item.payload).length > 0;
  const hasResult = item.result != null && Object.keys(item.result).length > 0;
  const isEmergencyOverride = item.eventType === 'case.emergency_override_used';
  const eventLabel = ACTIVITY_EVENT_LABELS[item.eventType] ?? humanizeEventType(item.eventType);

  return (
    <div
      data-testid={`timeline-activity-${item.id}`}
      className={
        isEmergencyOverride
          ? 'rounded-lg border border-warning/40 border-l-[3px] border-l-warning bg-warning/5 p-3'
          : 'flex flex-col items-center text-center'
      }
    >
      {isEmergencyOverride && (
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-warning">
            Emergency Override — quiet hours bypassed
          </span>
        </div>
      )}
      <div className="text-xs font-semibold text-muted-foreground" title={item.eventType}>
        {eventLabel}
        <span className="font-normal"> · {formatTimestamp(asIsoDateTimeString(item.createdAt), propertyTimezone)}</span>
      </div>
      {detailLines.length > 0 && (
        <ul
          data-testid={`activity-detail-${item.id}`}
          className="m-0 mt-1 w-full list-none space-y-1 p-0 text-sm text-muted-foreground"
        >
          {detailLines.map((line, i) => (
            <li key={i} className="break-words [overflow-wrap:anywhere]">{line}</li>
          ))}
        </ul>
      )}
      {(hasPayload || hasResult) && (
        <details
          data-testid={`activity-payload-expander-${item.id}`}
          open={expanded}
          onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
          className="mt-0.5 w-full text-left"
        >
          <summary className="cursor-pointer select-none text-center text-[11px] text-muted-foreground hover:text-foreground">
            {expanded ? 'Hide' : 'Show'} details
          </summary>
          <div
            className="mt-1.5 p-2 rounded overflow-x-auto"
            style={{ backgroundColor: colors.gray[900], border: `1px solid ${colors.gray[700]}` }}
          >
            <StructuredDataView
              data={hasResult ? { payload: item.payload, result: item.result } : item.payload}
            />
          </div>
        </details>
      )}
    </div>
  );
}
